/**
 * DragOverlay driven by dnd-kit's own active state, independent of the board's
 * drag state (which updates as a transition). Must render inside DndContext;
 * reads the context here because DragOverlay nullifies it for its children.
 */

import { memo, type ReactNode } from 'react';
import { DragOverlay, useDndContext, type DropAnimation } from '@dnd-kit/core';
import { KanbanOverlay } from './KanbanOverlay';
import type {
  KanbanDensity,
  KanbanItemBase,
  KanbanProps,
} from './Kanban.config.types';

interface KanbanDragOverlayProps<TItem extends KanbanItemBase> {
  itemsById: Map<string, TItem>;
  renderCard: KanbanProps<TItem>['renderCard'];
  density: KanbanDensity;
  dropAnimation: DropAnimation | null;
  /** Column drag preview; takes precedence over the card overlay. */
  columnOverlay: ReactNode;
}

/** DragOverlay's wrapper is already sized to the source card. */
const FILL_OVERLAY_WIDTH = '100%';
const NO_COLUMN_ITEM_IDS: string[] = [];
const noop = () => undefined;

function HeldCardInner<TItem extends KanbanItemBase>({
  item,
  renderCard,
  density,
}: {
  item: TItem;
  renderCard: KanbanProps<TItem>['renderCard'];
  density: KanbanDensity;
}) {
  return (
    <KanbanOverlay width={FILL_OVERLAY_WIDTH}>
      {renderCard(item, {
        density,
        isSelected: false,
        selectionEnabled: false,
        onToggleSelect: noop,
        selectionControl: null,
        isOverlay: true,
        columnItemIds: NO_COLUMN_ITEM_IDS,
      })}
    </KanbanOverlay>
  );
}

/** Pointer moves re-render the overlay every frame; the card itself must not. */
const HeldCard = memo(HeldCardInner) as typeof HeldCardInner;

export function KanbanDragOverlay<TItem extends KanbanItemBase>({
  itemsById,
  renderCard,
  density,
  dropAnimation,
  columnOverlay,
}: KanbanDragOverlayProps<TItem>) {
  const { active } = useDndContext();
  const heldItem =
    active?.data.current?.type === 'card'
      ? itemsById.get(String(active.id)) ?? null
      : null;

  return (
    <DragOverlay dropAnimation={dropAnimation}>
      {columnOverlay ??
        (heldItem ? (
          <HeldCard item={heldItem} renderCard={renderCard} density={density} />
        ) : null)}
    </DragOverlay>
  );
}
